import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js'
import type { CognitoUserSession as AmazonCognitoUserSession } from 'amazon-cognito-identity-js'
import { useUserStore } from '@/stores/userStore'
import { useNewPasswordDialog } from '@/composables/useNewPasswordDialog'

// Re-export the CognitoUserSession type for use in components
export type { CognitoUserSession } from 'amazon-cognito-identity-js'

import { POOL_DATA_CLIENT, POOL_DATA_COMPANY } from '@/env'

// SSO-specific authentication function for Company users (doesn't touch localStorage or stores)
export const authenticateForSSO = (
  username: string,
  password: string
): Promise<AmazonCognitoUserSession> => {
  return new Promise<AmazonCognitoUserSession>((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    const user = new CognitoUser({
      Username: username,
      Pool: new CognitoUserPool(POOL_DATA_COMPANY),
    })

    user.authenticateUser(authDetails, {
      onSuccess: (session: AmazonCognitoUserSession) => {
        console.log('SSO Authentication (Company) successful!')
        resolve(session)
      },
      onFailure: (err: Error) => {
        console.error('SSO Authentication (Company) failed:', err)
        reject(err)
      },
      newPasswordRequired: () => {
        reject(
          new Error(
            'Password change required. Please login to JAMS directly to change your password.'
          )
        )
      },
    })
  })
}

// SSO-specific authentication function for Client users (uses client Cognito pool)
export const authenticateForSSOClient = (
  username: string,
  password: string
): Promise<AmazonCognitoUserSession> => {
  return new Promise<AmazonCognitoUserSession>((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    const user = new CognitoUser({
      Username: username,
      Pool: new CognitoUserPool(POOL_DATA_CLIENT),
    })

    user.authenticateUser(authDetails, {
      onSuccess: (session: AmazonCognitoUserSession) => {
        console.log('SSO Authentication (Client) successful!')
        resolve(session)
      },
      onFailure: (err: Error) => {
        console.error('SSO Authentication (Client) failed:', err)
        reject(err)
      },
      newPasswordRequired: () => {
        reject(
          new Error(
            'Password change required. Please login to JAMS directly to change your password.'
          )
        )
      },
    })
  })
}

import Cookies from 'universal-cookie'

const cookies = new Cookies()

const userPool = new CognitoUserPool(POOL_DATA_CLIENT)

export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser()
    const userStore = useUserStore()

    if (!cognitoUser) {
      return reject(new Error('No user is currently signed in'))
    }

    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session.isValid()) {
        return reject(err || new Error('Session is invalid'))
      }

      // First, change the password
      cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          return reject(err)
        }

        // ✅ Then, update the custom attribute
        const updatedAttribute = new CognitoUserAttribute({
          Name: 'custom:isPasswordChanged',
          Value: '1',
        })
        userStore.setIsPasswordChanged('1')

        cognitoUser.updateAttributes([updatedAttribute], (err, result) => {
          if (err) {
            return reject(err)
          }

          // Optional: Sign out after everything is done
          cognitoUser.signOut()
          resolve()
        })
      })
    })
  })
}

// 🔹 Sign-in Function (Using Username & Password Auth)
export const signIn = (username: string, password: string): Promise<AmazonCognitoUserSession> => {
  return new Promise<AmazonCognitoUserSession>((resolve, reject) => {
    const userStore = useUserStore()

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    })

    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        console.log('Sign-in successful!', session)
        const idToken = session?.getIdToken()?.getJwtToken()
        const refreshToken = session.getRefreshToken().getToken()
        const role = session?.getAccessToken()?.payload?.['cognito:groups']?.[0]
        const juid = session?.getIdToken()?.payload?.['custom:juid']
        const isPasswordChanged = session?.getIdToken()?.payload?.['custom:isPasswordChanged']
        console.log(juid)
        console.log('Role got', role)

        userStore.setToken(idToken)
        userStore.setRefresh(refreshToken)
        userStore.setRole(role)
        userStore.setJuid(juid)
        userStore.setUsername(username)
        userStore.setIsPasswordChanged(isPasswordChanged)

        const userData = {
          idToken,
          role,
          juid,
          username,
        }
        cookies.remove('admin', { path: '/', domain: 'deploy-dev.d1bexjjr9l7jso.amplifyapp.com' })

        console.log('User authenticated and Axios configured!')

        resolve(session)
      },
      onFailure: (err) => {
        console.error('Sign-in failed:', err)
        reject(err)
        console.log(err)
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // User needs to set a new password (created by admin with temporary password)
        // Open the dialog and pass the challenge details
        const { openDialog } = useNewPasswordDialog()

        openDialog({
          user,
          userAttributes,
          requiredAttributes,
          resolve: (session) => {
            // User is now fully signed in with new password
            console.log('Password changed successfully, user signed in', session)
            const idToken = session?.getIdToken()?.getJwtToken()
            const refreshToken = session.getRefreshToken().getToken()
            const role = session?.getAccessToken()?.payload?.['cognito:groups']?.[0]
            const juid = session?.getIdToken()?.payload?.['custom:juid']
            const isPasswordChanged = session?.getIdToken()?.payload?.['custom:isPasswordChanged']

            userStore.setToken(idToken)
            userStore.setRefresh(refreshToken)
            userStore.setRole(role)
            userStore.setJuid(juid)
            userStore.setUsername(username)
            userStore.setIsPasswordChanged(isPasswordChanged)

            const userData = {
              idToken,
              role,
              juid,
              username,
            }
            cookies.remove('admin', {
              path: '/',
              domain: 'deploy-dev.d1bexjjr9l7jso.amplifyapp.com',
            })

            resolve(session)
          },
          reject: (err) => {
            console.error('Failed to set new password:', err)
            reject(err)
          },
        })
      },
    })
  })
}

export const forgotPassword = (username: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: userPool })

    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      // optional: onCodeSent callback
      inputVerificationCode: (data) => {
        console.log('Code sent to:', data) // tell UI to show code+new-password fields
        resolve()
      },
    })
  })

export const resetPassword = (username: string, code: string, newPassword: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: userPool })
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })

const adminPool = new CognitoUserPool(POOL_DATA_COMPANY)

export function changePasswordAdmin(oldPassword: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = adminPool.getCurrentUser()
    const userStore = useUserStore()

    if (!cognitoUser) {
      return reject(new Error('No user is currently signed in'))
    }

    // First, you need a valid session
    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session.isValid()) {
        return reject(err || new Error('Session is invalid'))
      }

      // Step 1: Change the password
      cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          return reject(err)
        }

        // Step 2: Update custom attribute
        const updatedAttribute = new CognitoUserAttribute({
          Name: 'custom:isPasswordChanged',
          Value: '1',
        })

        cognitoUser.updateAttributes([updatedAttribute], (err, result) => {
          if (err) {
            return reject(err)
          }

          // Step 3: Update local store
          userStore.setIsPasswordChanged('1') // ✅ Correct value

          // Step 4: Optional sign out
          cognitoUser.signOut()

          resolve()
        })
      })
    })
  })
}

export const signInAdmin = (
  username: string,
  password: string
): Promise<AmazonCognitoUserSession> => {
  return new Promise<AmazonCognitoUserSession>((resolve, reject) => {
    const userStore = useUserStore()

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    })

    const user = new CognitoUser({
      Username: username,
      Pool: adminPool,
    })

    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        console.log('Sign-in successful!', session)
        const idToken = session?.getIdToken()?.getJwtToken()
        const refreshToken = session.getRefreshToken().getToken()
        const role = session?.getAccessToken()?.payload?.['cognito:groups']?.[0]
        const juid = session?.getIdToken()?.payload?.['custom:juid']
        const isPasswordChanged = session?.getIdToken()?.payload?.['custom:isPasswordChanged']
        console.log(juid)
        console.log('Role got', role)

        userStore.setToken(idToken)
        userStore.setRefresh(refreshToken)
        userStore.setRole(role)
        userStore.setJuid(juid)
        userStore.setUsername(username)
        userStore.setIsPasswordChanged(isPasswordChanged)

        const userData = {
          idToken,
          role,
          username,
        }

        console.log('User authenticated and Axios configured!')

        resolve(session)
      },
      onFailure: (err) => {
        console.error('Sign-in failed:', err)
        reject(err)
        console.log(err)
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Open the dialog and pass the challenge details
        const { openDialog } = useNewPasswordDialog()

        openDialog({
          user,
          userAttributes,
          requiredAttributes,
          resolve: (session) => {
            // Now they're fully signed in—run your onSuccess logic here too
            const idToken = session.getIdToken().getJwtToken()
            const refreshToken = session.getRefreshToken().getToken()
            const role = session.getAccessToken().payload?.['cognito:groups']?.[0]

            userStore.setToken(idToken)
            userStore.setRefresh(refreshToken)
            userStore.setRole(role)

            resolve(session)
          },
          reject: (err) => {
            console.error('Failed to set new password:', err)
            reject(err)
          },
        })
      },
    })
  })
}

const adminPoolForgot = new CognitoUserPool(POOL_DATA_COMPANY)

export const forgotPasswordAdmin = (username: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: adminPoolForgot })

    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      // optional: onCodeSent callback
      inputVerificationCode: (data) => {
        console.log('Code sent to:', data) // tell UI to show code+new-password fields
        resolve()
      },
    })
  })

export const resetPasswordAdmin = (username: string, code: string, newPassword: string) =>
  new Promise<void>((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: adminPoolForgot })
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    })
  })

export const refreshToken = () => {
  return new Promise((resolve, reject) => {
    const currentUser = userPool.getCurrentUser()
    const userStore = useUserStore()

    if (!currentUser) {
      reject(new Error('No user found'))
      return
    }

    currentUser.getSession((err: any, session: AmazonCognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        console.log('Session expired, refreshing...')

        if (!userStore?.refresh) {
          reject(new Error('No refresh token available'))
          return
        }

        const refreshToken = new CognitoRefreshToken({
          RefreshToken: userStore.refresh,
        })

        currentUser.refreshSession(refreshToken, (err, newSession) => {
          if (err) {
            console.error('Failed to refresh token:', err)
            reject(err)
          } else {
            console.log('Token refreshed!', newSession)
            userStore.setToken(newSession.getIdToken.getJwtToken)
            userStore.setRefresh(newSession.getRefreshToken.getToken)

            // localStorage.setItem("idToken", newSession.getIdToken().getJwtToken());
            // localStorage.setItem("accessToken", newSession.getAccessToken().getJwtToken());
            // localStorage.setItem("refreshToken", newSession.getRefreshToken().getToken());

            resolve(newSession)
          }
        })
      } else {
        resolve(session)
      }
    })
  })
}
